import { useEffect, useRef } from 'react';

interface UseWebSocketReconnectionOptions {
  delays: number[]; // Array of delays in milliseconds
  onReconnect: () => Promise<void>;
  shouldReconnect: boolean;
  enabled: boolean;
}

export const useWebSocketReconnection = ({
  delays,
  onReconnect,
  shouldReconnect,
  enabled,
}: UseWebSocketReconnectionOptions) => {
  const retryAttempt = useRef<number>(0);
  const retryTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (shouldReconnect && enabled) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }

      const currentDelay = delays[retryAttempt.current % delays.length];
      console.log(`Reconnection attempt ${retryAttempt.current + 1} in ${currentDelay}ms`);

      retryTimeout.current = setTimeout(async () => {
        try {
          await onReconnect();
          retryAttempt.current = 0; // Reset on successful connection
        } catch (error) {
          console.error('Reconnection failed:', error);
          retryAttempt.current++;
        }
      }, currentDelay);
    }

    return () => {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
    };
  }, [shouldReconnect, enabled, delays, onReconnect]);

  const incrementRetryAttempt = () => {
    retryAttempt.current++;
  };

  const resetRetryAttempt = () => {
    retryAttempt.current = 0;
  };

  return {
    currentAttempt: retryAttempt.current,
    incrementRetryAttempt,
    resetRetryAttempt,
  };
};
